import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getVisitorId, getSessionId, trackEvent } from "../../lib/track";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SKIP_PREFIXES = ["/admin", "/login", "/partnerportal"];

// Routes that represent sales collateral / document views.
const COLLATERAL_PATTERNS = [/^\/collateral\//, /^\/big-bad-mats-/];

function isCollateral(path) {
  if (/calculator/.test(path)) return false;
  return COLLATERAL_PATTERNS.some((re) => re.test(path));
}

export default function PageTracker() {
  const location = useLocation();
  const enter = useRef({ path: null, t: 0, maxScroll: 0 });

  // Track max scroll depth for the current page.
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? Math.min(100, Math.round((window.scrollY / h) * 100)) : 0;
      if (pct > enter.current.maxScroll) enter.current.maxScroll = pct;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => location.pathname.startsWith(p))) return;

    // Flush engagement for the page we're leaving.
    const prev = enter.current;
    if (prev.path && !SKIP_PREFIXES.some((p) => prev.path.startsWith(p))) {
      const duration = Math.round((Date.now() - prev.t) / 1000);
      if (duration >= 10 || prev.maxScroll >= 40) {
        trackEvent("page_engagement", {
          path: prev.path,
          meta: { duration_s: duration, scroll_pct: prev.maxScroll },
        });
      }
    }
    enter.current = { path: location.pathname, t: Date.now(), maxScroll: 0 };

    const ref = document.referrer && !document.referrer.includes(window.location.host)
      ? document.referrer
      : "direct";
    try {
      fetch(`${BACKEND}/api/track/pageview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: location.pathname, referrer: ref, session_id: getSessionId(), visitor_id: getVisitorId() }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* no-op */ }

    if (isCollateral(location.pathname)) {
      trackEvent("collateral_view", { path: location.pathname, label: `Collateral view: ${location.pathname}` });
    }
  }, [location.pathname]);

  // Flush engagement on tab close / unload.
  useEffect(() => {
    const flush = () => {
      const prev = enter.current;
      if (!prev.path || SKIP_PREFIXES.some((p) => prev.path.startsWith(p))) return;
      const duration = Math.round((Date.now() - prev.t) / 1000);
      if (duration >= 10 || prev.maxScroll >= 40) {
        trackEvent("page_engagement", {
          path: prev.path,
          meta: { duration_s: duration, scroll_pct: prev.maxScroll },
        });
      }
    };
    window.addEventListener("beforeunload", flush);
    return () => window.removeEventListener("beforeunload", flush);
  }, []);

  return null;
}
